import { Link } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { useStore } from "@/context/StoreContext";
import NewsletterForm from "@/components/NewsletterForm";
import { SOCIAL_ICONS } from "@/components/SocialIcons";
import DiagnosticsConsole from "@/components/DiagnosticsConsole";
import ContactButton from "@/components/ContactButton";

const SOCIAL_ORDER = ["instagram", "tiktok", "x", "facebook", "pinterest", "youtube"];

export default function Footer() {
  const { t } = useLang();
  const { settings } = useStore();
  const socials = SOCIAL_ORDER.filter((k) => settings[k]);
  return (
    <footer className="relative border-t border-bone/10 bg-ink-2">
      <div className="bp-grid pointer-events-none absolute inset-0 opacity-50" />
      <div className="relative mx-auto max-w-7xl px-6 py-16 sm:px-10 lg:px-16">
        <div className="flex flex-col justify-between gap-12 md:flex-row">
          <div>
            <p className="font-cursive text-5xl text-bone">DeMoivre</p>
            <p className="mt-3 font-mono text-[10px] tracking-[0.35em] text-brass">
              {t("brand.taglineCaps")}
            </p>
            <p className="mt-6 max-w-xs text-sm leading-relaxed text-bone-dim/80">
              {t("footer.blurb")}
            </p>
            <p className="eyebrow mb-4 mt-10">{t("newsletter.title")}</p>
            <p className="mb-4 max-w-xs text-xs leading-relaxed text-bone-dim/70">{t("newsletter.text")}</p>
            <NewsletterForm />
          </div>
          <div className="grid grid-cols-2 gap-12 sm:gap-20">
            <div>
              <p className="eyebrow mb-5">{t("footer.atelier")}</p>
              <ul className="space-y-3 font-mono text-[11px] tracking-[0.2em] text-bone-dim">
                <li><Link data-testid="footer-shop-link" to="/shop" className="transition-colors hover:text-brass">{t("footer.shop")}</Link></li>
                <li><Link data-testid="footer-portfolio-link" to="/portfolio" className="transition-colors hover:text-brass">{t("footer.portfolio")}</Link></li>
                <li><Link data-testid="footer-process-link" to="/process" className="transition-colors hover:text-brass">{t("footer.process")}</Link></li>
                <li><Link data-testid="footer-about-link" to="/about" className="transition-colors hover:text-brass">{t("footer.about")}</Link></li>
              </ul>
            </div>
            <div>
              <p className="eyebrow mb-5">{t("footer.contact")}</p>
              <ContactButton testId="footer-contact-button" className="mb-5 w-fit" />
              <ul className="space-y-3 font-mono text-[11px] tracking-[0.2em] text-bone-dim">
                <li><Link data-testid="footer-commissions-link" to="/commissions" className="transition-colors hover:text-brass">{t("footer.commissions")}</Link></li>
                <li><Link data-testid="footer-admin-link" to="/admin" className="transition-colors hover:text-brass">{t("footer.admin")}</Link></li>
                {settings.contact_email && <li className="pt-3"><a data-testid="footer-contact-email" href={`mailto:${settings.contact_email}`} className="[overflow-wrap:anywhere] tracking-[0.05em] text-bone-muted transition-colors hover:text-brass sm:tracking-[0.1em]">{settings.contact_email}</a></li>}
                {settings.contact_phone && <li><a data-testid="footer-contact-phone" href={`tel:${settings.contact_phone.replace(/[^+\d]/g, "")}`} className="text-bone-muted transition-colors hover:text-brass">{settings.contact_phone}</a></li>}
                {settings.contact_whatsapp && <li><a data-testid="footer-contact-whatsapp" href={`https://wa.me/${settings.contact_whatsapp.replace(/[^\d]/g, "")}`} target="_blank" rel="noopener noreferrer" className="text-bone-muted transition-colors hover:text-brass">WHATSAPP</a></li>}
                {settings.contact_address && <li data-testid="footer-contact-address" className="whitespace-pre-line break-words normal-case tracking-[0.1em] text-bone-muted">{settings.contact_address}</li>}
              </ul>
              {socials.length > 0 && (
                <div data-testid="footer-socials" className="mt-8 flex flex-wrap gap-2">
                  {socials.map((k) => {
                    const Icon = SOCIAL_ICONS[k];
                    return (
                      <a
                        key={k}
                        data-testid={`footer-social-${k}`}
                        href={settings[k]}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={k}
                        className="flex h-9 w-9 items-center justify-center border border-bone/15 text-bone-dim transition-colors duration-300 hover:border-brass/60 hover:text-brass"
                      >
                        <Icon size={14} />
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="mt-16 flex flex-col items-start justify-between gap-4 border-t border-bone/10 pt-8 font-mono text-[9px] tracking-[0.3em] text-bone-muted sm:flex-row sm:items-center">
          <span>{t("footer.rights")}</span>
          <span>DWG-SITE-FR-001 · ÉCHELLE 1:1</span>
        </div>
      </div>
      <DiagnosticsConsole />
    </footer>
  );
}
